import { api } from "./api";

export interface RuntimeConfig {
  publicBaseUrl: string;
  env: string;
  autoModels: string[];
}

let cached: Promise<RuntimeConfig> | null = null;

/**
 * The developer-facing NEXUS base URL — read from server config
 * (`PUBLIC_BASE_URL`), falling back to the console's own origin, which is the
 * same host the API is served from. Never fabricated.
 */
export function loadRuntimeConfig(): Promise<RuntimeConfig> {
  cached ??= api<RuntimeConfig>("/api/config").catch(() => ({
    publicBaseUrl: window.location.origin,
    env: "development",
    autoModels: [...POLICY_ORDER],
  }));
  return cached;
}

/* ── Routing policies (virtual models) ─────────────────────────── */

export const POLICY_ORDER = [
  "auto",
  "free-auto",
  "coding-auto",
  "vision-auto",
  "embedding-auto",
  "budget-auto",
  "fast-auto",
] as const;

export const POLICY_META: Record<
  string,
  { name: string; tagline: string; detail: string }
> = {
  auto: {
    name: "NEXUS Auto",
    tagline: "Best available model",
    detail:
      "NEXUS scores every eligible deployment on capability, health, latency, and cost — and picks the best one for each request.",
  },
  "free-auto": {
    name: "NEXUS Free",
    tagline: "Best eligible free model",
    detail:
      "Routes only to free-tier capacity. Falls back within the free pool — never silently upgrades you to a paid deployment.",
  },
  "coding-auto": {
    name: "NEXUS Coding",
    tagline: "Optimized for code",
    detail:
      "Prefers models with high coding scores, tool use, and JSON/structured output.",
  },
  "vision-auto": {
    name: "NEXUS Vision",
    tagline: "Image-capable models",
    detail: "Routes to models with vision capability for image inputs.",
  },
  "embedding-auto": {
    name: "NEXUS Embedding",
    tagline: "Text embeddings",
    detail: "Routes embedding requests to the best available embedding model.",
  },
  "budget-auto": {
    name: "NEXUS Budget",
    tagline: "Free-first, cost-safe",
    detail:
      "Same free-first behavior as free-auto — a clearer name for cost-constrained workloads.",
  },
  "fast-auto": {
    name: "NEXUS Fast",
    tagline: "Lowest expected latency",
    detail: "Optimizes for response speed — lowest measured p50 wins.",
  },
};

export function isPolicyModel(id: string | null | undefined): boolean {
  return !!id && (POLICY_ORDER as readonly string[]).includes(id);
}

/* ── Persisted default model (UI preference — no schema) ───────── */

const DEFAULT_MODEL_KEY = "infermesh.defaultModel";

export function getDefaultModel(): string {
  return localStorage.getItem(DEFAULT_MODEL_KEY) ?? "auto";
}

export function setDefaultModel(id: string): void {
  if (id) localStorage.setItem(DEFAULT_MODEL_KEY, id);
  else localStorage.removeItem(DEFAULT_MODEL_KEY);
}

/* ── Code snippets — generated from the real contract ──────────── */

export interface SnippetInput {
  baseUrl: string;
  model: string;
  prompt: string;
  stream: boolean;
}

export function curlSnippet({ baseUrl, model, prompt, stream }: SnippetInput): string {
  const body = {
    model,
    messages: [{ role: "user", content: prompt || "Hello" }],
    ...(stream ? { stream: true } : {}),
  };
  return `curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer $NEXUS_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(body)}'`;
}

export function jsSnippet({ baseUrl, model, prompt, stream }: SnippetInput): string {
  return `const res = await fetch("${baseUrl}/v1/chat/completions", {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${process.env.NEXUS_API_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    model: "${model}",
    messages: [{ role: "user", content: ${JSON.stringify(prompt || "Hello")} }],
${stream ? "    stream: true,\n" : ""}  }),
});
${stream ? "// Server-Sent Events — see streaming docs\ndocument.body; res.body" : "const data = await res.json();\nconsole.log(data.choices[0].message.content);"}
// NEXUS route: res.headers.get("x-infermesh-provider") / "x-infermesh-model"`;
}

export function openaiSdkSnippet({ baseUrl, model, prompt }: SnippetInput): string {
  return `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.NEXUS_API_KEY,
  baseURL: "${baseUrl}/v1",
});

const response = await client.chat.completions.create({
  model: "${model}",
  messages: [{ role: "user", content: ${JSON.stringify(prompt || "Hello, NEXUS")} }],
});

console.log(response.choices[0].message.content);`;
}

export function pySnippet({ baseUrl, model, prompt, stream }: SnippetInput): string {
  return `import os, requests

res = requests.post(
    "${baseUrl}/v1/chat/completions",
    headers={"Authorization": f"Bearer {os.environ['NEXUS_API_KEY']}"},
    json={
        "model": "${model}",
        "messages": [{"role": "user", "content": ${JSON.stringify(prompt || "Hello")}}],
${stream ? '        "stream": True,\n' : ""}    },
)
${stream ? "for line in res.iter_lines(): print(line)  # SSE" : "print(res.json()['choices'][0]['message']['content'])"}
# NEXUS route: res.headers["x-infermesh-provider"] / ["x-infermesh-model"]`;
}
